import {
  DashboardBasicAccountApplicationDocument,
  DashboardCardApplicationDocument,
  DashboardDocument,
} from "../../api/commercial/generated-types";
import { ProductFamily } from "../../context/product-context/product-family";
import { Product } from "../../context/product-context/product";

/**
 * Given all finished applications this function will return the latest one. (By the last modified property)
 *
 * @param finishedApplications The finished Applications.
 * @return DashboardBasicAccountApplicationDocument | DashboardCardApplicationDocument The last updated Application.
 */
const getLatestModifiedApplication = (
  finishedApplications: DashboardDocument,
):
  | DashboardBasicAccountApplicationDocument
  | DashboardCardApplicationDocument => {
  const allApplications = []
    .concat(finishedApplications.basicAccountApplications)
    .concat(finishedApplications.cardApplications);

  // get last modified application. Sort by DESC.
  return allApplications.sort(
    (a, b) =>
      Math.abs(Number(new Date(a.lastModifiedDate))) -
      Math.abs(Number(new Date(b.lastModifiedDate))),
  )[allApplications.length - 1];
};

/**
 * This function creates a new family that will show hidden products only if a card application is done by the user.
 *
 * @param selectedFamily
 * @param cardholderApplications
 */
const getFamilyWithCompletedProducts = (
  selectedFamily: ProductFamily,
  cardholderApplications?: DashboardCardApplicationDocument[],
): ProductFamily => {
  const visibleAndCompletedProducts: Product[] = selectedFamily.allProducts.map(
    (product: Product) => {
      const copyProduct = Object.assign({}, product);
      if (product.shouldBeShown) {
        return copyProduct;
      }
      const matchedProductWithCard = cardholderApplications.find(
        (ca: DashboardCardApplicationDocument) => {
          return ca.productRef === product.productRef;
        },
      );
      if (matchedProductWithCard !== undefined) {
        copyProduct.shouldBeShown = true;
        return copyProduct;
      }
      return copyProduct;
    },
  );
  const copyFamily: ProductFamily = Object.assign({}, selectedFamily);
  copyFamily.products = visibleAndCompletedProducts;
  return copyFamily;
};

const getAllProductsFromFamily = (
  family: ProductFamily,
  completedProducts: Product[],
): Product[] => {
  const hiddenProducts: Product[] | undefined = family.getHiddenProducts();
  const completedDisabledProducts = completedProducts.filter(
    (product) => product.family === family && !product.isActive,
  );

  if (!hiddenProducts) {
    return [
      ...family.getAllAvailableAndShownProducts(),
      ...completedDisabledProducts,
    ];
  }
  // noinspection LocalVariableNamingConventionJS
  const hasOverlapBetweenCompletedAndHiddenProducts = completedProducts.find(
    (completedProduct: Product) => {
      return Boolean(
        hiddenProducts.find((hiddenProduct: Product) => {
          return hiddenProduct.productRef === completedProduct.productRef;
        }),
      );
    },
  );
  if (hasOverlapBetweenCompletedAndHiddenProducts) {
    return [...family.getAllAvailableProducts(), ...completedDisabledProducts];
  }
  return [
    ...family.getAllAvailableAndShownProducts(),
    ...completedDisabledProducts,
  ];
};

const DashboardHelper = {
  getLatestModifiedApplication,
  getFamilyWithCompletedProducts,
  getAllProductsFromFamily,
};

export default DashboardHelper;
